/* eslint-disable require-atomic-updates */
import { useCallback, useEffect, useRef } from 'react'
import { useLazyQuery, useReactiveVar } from '@apollo/client'
import {
  GetDemoUpDownAiQuotaQuery,
  GetGuestSessionQuery
} from '__generated__/graphql'
import { GET_GUEST_SESSION } from 'api/guest-session/get-guest-session'
import { GET_DEMO_UP_DOWN_AI_QUOTA } from 'api/mode-up-down-ai-demo/get-demo-up-down-ai-quota'
import { isGuestClaimDialogOpenVar } from 'shared/store/dialogs'
import { userVar } from 'shared/store/user'

const hasUnclaimedDemoRounds = (
  session: GetGuestSessionQuery['getGuestSession'],
  quota?: GetDemoUpDownAiQuotaQuery['getDemoUpDownAiQuota'] | null
) => {
  if (!session?.id || session.claimedAt) return false

  return (quota?.roundsPlayed ?? 0) > 0
}

export const useMergeGuestSession = () => {
  const user = useReactiveVar(userVar)
  const promptedForUserRef = useRef<string | null>(null)
  const [getGuestSession] = useLazyQuery<GetGuestSessionQuery>(
    GET_GUEST_SESSION,
    { fetchPolicy: 'no-cache' }
  )
  const [getDemoUpDownAiQuota] = useLazyQuery<GetDemoUpDownAiQuotaQuery>(
    GET_DEMO_UP_DOWN_AI_QUOTA,
    { fetchPolicy: 'no-cache' }
  )

  const promptIfNeeded = useCallback(async () => {
    if (!user?.id || promptedForUserRef.current === user.id) return

    const [sessionResult, quotaResult] = await Promise.all([
      getGuestSession(),
      getDemoUpDownAiQuota()
    ])
    promptedForUserRef.current = user.id

    if (
      !hasUnclaimedDemoRounds(
        sessionResult.data?.getGuestSession,
        quotaResult.data?.getDemoUpDownAiQuota
      )
    ) {
      return
    }

    isGuestClaimDialogOpenVar(true)
  }, [getDemoUpDownAiQuota, getGuestSession, user?.id])

  useEffect(() => {
    if (!user?.id) {
      promptedForUserRef.current = null
      isGuestClaimDialogOpenVar(false)
      return
    }

    void promptIfNeeded()
  }, [promptIfNeeded, user?.id])
}
